import { isDebug, logNumberOfHistory } from '@/constants/env';

/**
 * ログレベル
 */
export const LogLevel = {
  /** エラー */
  Error: 1,
  /** 警告 */
  Warn: 2,
  /** Info */
  Info: 3,
  /** デバッグ */
  Debug: 4,
} as const;

/**
 * ログレベル
 */
export type LogLevelType = typeof LogLevel[keyof typeof LogLevel];

/**
 * ロガー
 */
export class Logger {
  /**
   * コンストラクタ
   * @param logLevel ログレベルを指定
   */
  constructor(logLevel: LogLevelType) {
    this.logLevel = logLevel;
    this.limit = logNumberOfHistory;
    this.logList = [];
  }

  /**
   * エラー
   * @param message メッセージ
   */
  error(message: string) {
    if (this.logLevel >= LogLevel.Error) {
      // eslint-disable-next-line no-console
      console.error(message);
      this.addLog(`ERROR: ${message}`);
    }
  }

  /**
   * 警告
   * @param message メッセージ
   */
  warn(message: string) {
    if (this.logLevel >= LogLevel.Warn) {
      // eslint-disable-next-line no-console
      console.warn(message);
      this.addLog(`WARN : ${message}`);
    }
  }

  /**
   * Info
   * @param message メッセージ
   */
  info(message: string) {
    if (this.logLevel >= LogLevel.Info) {
      if (isDebug) {
        // eslint-disable-next-line no-console
        console.log(message);
      }
      this.addLog(`INFO : ${message}`);
    }
  }

  /**
   * デバッグ
   * @param message メッセージ
   */
  debug(message: string) {
    if (this.logLevel >= LogLevel.Debug) {
      if (isDebug) {
        // eslint-disable-next-line no-console
        console.debug(message);
      }
      this.addLog(`DEBUG: ${message}`);
    }
  }

  /**
   * ログを出力します.
   * @param delimiter 区切り文字
   * @returns ログ.
   */
  outputLog(delimiter = '\n'): string {
    let text = '';
    for (let i = 0; i < this.logList.length; i += 1) {
      text += `${this.logList[i]}${delimiter}`;
    }
    return text;
  }

  /**
   * ログクリア
   */
  clearLog() {
    this.logList = [];
  }

  /**
   * ログ追加
   * @param message メッセージ
   */
  protected addLog(message: string) {
    const date = new Date();
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    const h = date.getHours().toString().padStart(2, '0');
    const min = date.getMinutes().toString().padStart(2, '0');
    const s = date.getSeconds().toString().padStart(2, '0');
    const z = date.getMilliseconds().toString().padStart(3, '0');
    const dateText = `${y}-${m}-${d} ${h}:${min}:${s}.${z}`;
    this.logList.push(`${dateText} ${message}`);
    if (this.logList.length > this.limit) {
      this.logList = this.logList.slice(-this.limit);
    }
  }

  /** リミット */
  protected readonly limit: number;

  /** ログレベル */
  protected readonly logLevel: LogLevelType;

  /** ログリスト */
  protected logList: string[];
}
